import ky, { HTTPError } from 'ky';

/** Read XSRF-TOKEN cookie value (set by Spring Security's CookieCsrfTokenRepository). */
function readCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  if (!match) return null;
  const raw = match[1];
  return raw ? decodeURIComponent(raw) : null;
}

export const api = ky.create({
  prefixUrl: '/api',
  retry: 0,
  // Send + receive cookies (session + XSRF-TOKEN) on cross-port dev requests too.
  credentials: 'include',
  hooks: {
    beforeRequest: [
      (req) => {
        // Send the X-XSRF-TOKEN header on every mutating request — Spring's
        // CSRF chain reads it and matches against the cookie value.
        if (req.method !== 'GET' && req.method !== 'HEAD') {
          const token = readCsrfToken();
          if (token) req.headers.set('X-XSRF-TOKEN', token);
        }
      },
    ],
    afterResponse: [
      async (req, _opts, response) => {
        // 401/403 on any API call other than login itself = session lost or never
        // existed → hard-navigate to /login. Full reload clears any in-flight
        // suspended queries cleanly. Skip if already on /login.
        const isLoginCall = req.url.endsWith('/api/auth/login');
        const onLoginPage = window.location.pathname === '/login';
        if ((response.status === 401 || response.status === 403) && !isLoginCall && !onLoginPage) {
          window.location.href = '/login';
        }
        return response;
      },
    ],
  },
});

export type ApiError = {
  reason: string;
  message: string;
  ts: string;
  fieldErrors?: Array<{ field: string; message: string }>;
};

export async function parseError(error: unknown): Promise<ApiError> {
  if (error instanceof HTTPError) {
    try {
      return (await error.response.json()) as ApiError;
    } catch {
      return { reason: 'unknown', message: error.message, ts: new Date().toISOString() };
    }
  }
  if (error instanceof Error) {
    return { reason: 'unknown', message: error.message, ts: new Date().toISOString() };
  }
  return { reason: 'unknown', message: 'Unknown error', ts: new Date().toISOString() };
}
